"use client";

import React, { useState, useRef } from "react";
import {
  Text,
  Section,
  SectionCard,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Input,
} from "@/src/components";
import { cn } from "@/src/utils/utils";
import {
  MousePointerClick,
  Focus,
  Hand,
  ToggleRight,
  CheckSquare,
  ChevronsUpDown,
  ChevronDown,
  Loader2,
  Bell,
  Sparkles,
  AlertTriangle,
  X,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Code snippet helper
// ---------------------------------------------------------------------------

function CodeSnippet({ children }: { children: string }) {
  return (
    <div className="mt-3 bg-muted/50 rounded-lg p-3 font-mono text-xs overflow-x-auto">
      <pre className="whitespace-pre-wrap">{children}</pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Button Press
// ---------------------------------------------------------------------------

function ButtonPressDemo() {
  const [clicks, setClicks] = useState(0);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Buttons use <code className="bg-muted px-1 rounded">active:scale-[0.97]</code> for
        tactile press feedback. Try clicking each variant — notice the subtle shrink.
      </Text>
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setClicks((c) => c + 1)}>
          Primary ({clicks})
        </Button>
        <Button variant="outline" onClick={() => setClicks((c) => c + 1)}>
          Outline
        </Button>
        <Button variant="outline" onClick={() => setClicks((c) => c + 1)}>
          Secondary
        </Button>
        <Button variant="ghost" onClick={() => setClicks((c) => c + 1)}>
          Ghost
        </Button>
      </div>
      <CodeSnippet>{`// componentStyle.ts — button root
"... transition-all active:scale-[0.97]"`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Focus Rings
// ---------------------------------------------------------------------------

function FocusRingDemo() {
  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Focus rings use a 3px ring with <code className="bg-muted px-1 rounded">ring-ring/50</code> for
        a soft, accessible glow. Tab through the elements below to see them.
      </Text>
      <div className="flex flex-wrap items-center gap-4">
        <Button variant="outline">Tab to me</Button>
        <Input placeholder="Focus me..." className="w-48" />
        <Button variant="ghost" size="icon">
          <Focus className="h-4 w-4" />
        </Button>
      </div>
      <CodeSnippet>{`// Focus ring pattern
"focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Hover States
// ---------------------------------------------------------------------------

function HoverStatesDemo() {
  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Hover transitions use <code className="bg-muted px-1 rounded">transition-colors</code> for
        smooth background and text color changes. Hover over the items below.
      </Text>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["Dashboard", "Reports", "Settings"].map((item) => (
          <div
            key={item}
            className="rounded-lg border border-border p-4 cursor-pointer transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Text className="font-medium text-sm">{item}</Text>
            <Text className="text-xs text-muted-foreground mt-1">Hover to preview</Text>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        {["View invoices", "Create payment", "Run report"].map((action) => (
          <div
            key={action}
            className="rounded-md px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-accent"
          >
            {action}
          </div>
        ))}
      </div>
      <CodeSnippet>{`// Card hover
"transition-colors hover:bg-accent hover:text-accent-foreground"

// List item hover
"transition-colors hover:bg-accent"`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Toggle / Switch
// ---------------------------------------------------------------------------

function ToggleSwitchDemo() {
  const [on, setOn] = useState(false);
  const [on2, setOn2] = useState(true);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Toggle switches animate the knob position with
        <code className="bg-muted px-1 rounded ml-1">transition-transform</code> and
        background color with <code className="bg-muted px-1 rounded">transition-colors</code>.
      </Text>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => setOn(!on)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              on ? "bg-primary" : "bg-muted"
            )}
          >
            <span
              className={cn(
                "pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg transition-transform",
                on ? "translate-x-5" : "translate-x-0"
              )}
            />
          </button>
          <Text className="text-sm">{on ? "On" : "Off"}</Text>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={on2}
            onClick={() => setOn2(!on2)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              on2 ? "bg-primary" : "bg-muted"
            )}
          >
            <span
              className={cn(
                "pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg transition-transform",
                on2 ? "translate-x-5" : "translate-x-0"
              )}
            />
          </button>
          <Text className="text-sm">Notifications {on2 ? "enabled" : "disabled"}</Text>
        </div>
      </div>
      <CodeSnippet>{`// Switch knob
"transition-transform"
on ? "translate-x-5" : "translate-x-0"

// Switch track
"transition-colors"
on ? "bg-primary" : "bg-muted"`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5. Checkbox Stroke
// ---------------------------------------------------------------------------

function CheckboxStrokeDemo() {
  const [checked, setChecked] = useState(false);
  const [key, setKey] = useState(0);

  const toggle = () => {
    if (!checked) {
      setKey((k) => k + 1);
      setChecked(true);
    } else {
      setChecked(false);
    }
  };

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        The checkmark uses an SVG stroke animation via
        <code className="bg-muted px-1 rounded ml-1">.animate-check-stroke</code>.
        The stroke-dashoffset transitions from 24 to 0 for a drawing effect.
      </Text>
      <div className="flex items-center gap-6">
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          onClick={toggle}
          className={cn(
            "flex items-center justify-center h-5 w-5 rounded border-2 transition-colors cursor-pointer",
            checked ? "bg-primary border-primary" : "border-muted-foreground/40"
          )}
        >
          {checked && (
            <svg key={key} viewBox="0 0 12 12" className="h-3 w-3 text-primary-foreground">
              <path
                d="M2 6l3 3 5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="animate-check-stroke"
              />
            </svg>
          )}
        </button>
        <Text className="text-sm">{checked ? "Checked" : "Unchecked"} — click to toggle</Text>
      </div>
      <CodeSnippet>{`@keyframes check-stroke {
  from { stroke-dashoffset: 24; }
  to { stroke-dashoffset: 0; }
}

.animate-check-stroke {
  stroke-dasharray: 24;
  animation: check-stroke 300ms var(--ease-smooth) 100ms both;
}`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6. Accordion Expand
// ---------------------------------------------------------------------------

function AccordionDemo() {
  const [open, setOpen] = useState<string | null>(null);

  const items = [
    { id: "a1", title: "What is BlueCopa?", content: "A comprehensive component library for finance process applications, built on shadcn/ui with ECharts, AG Grid, and token-driven theming." },
    { id: "a2", title: "How does motion work?", content: "All animations are CSS-first using keyframes and custom properties. No runtime animation library is needed. Reduced motion is automatically respected." },
    { id: "a3", title: "Can I customize animations?", content: "Yes — override the CSS custom properties (--duration-enter, --ease-smooth, etc.) or create your own keyframes. The token system makes customization seamless." },
  ];

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Accordion uses CSS Grid's <code className="bg-muted px-1 rounded">grid-template-rows: 0fr → 1fr</code> trick
        for smooth height animation. The chevron rotates with <code className="bg-muted px-1 rounded">transition-transform</code>.
      </Text>
      <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
        {items.map((item) => {
          const isOpen = open === item.id;
          return (
            <div key={item.id}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : item.id)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-accent/50 transition-colors"
              >
                {item.title}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    isOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                className="grid transition-all duration-200 ease-out"
                style={{
                  gridTemplateRows: isOpen ? "1fr" : "0fr",
                }}
              >
                <div className="overflow-hidden">
                  <div className="px-4 pb-3 text-sm text-muted-foreground">
                    {item.content}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <CodeSnippet>{`// Smooth height with CSS Grid
<div style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
     className="grid transition-all duration-200">
  <div className="overflow-hidden">
    {content}
  </div>
</div>`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7. Dropdown / Menu
// ---------------------------------------------------------------------------

function DropdownDemo() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Dropdowns enter with <code className="bg-muted px-1 rounded">.animate-fade-in-down</code> — a
        subtle 6px slide-down combined with opacity fade. Duration: 250ms.
      </Text>
      <div className="relative inline-block" ref={ref}>
        <Button variant="outline" onClick={() => setOpen(!open)}>
          Actions
          <ChevronDown className={cn("h-4 w-4 ml-1 transition-transform duration-200", open && "rotate-180")} />
        </Button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-48 rounded-lg border border-border bg-background shadow-lg z-10 animate-fade-in-down">
            <div className="py-1">
              {["View details", "Edit record", "Export PDF", "Delete"].map((item, i) => (
                <button
                  key={item}
                  type="button"
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent",
                    item === "Delete" && "text-destructive"
                  )}
                  onClick={() => setOpen(false)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <CodeSnippet>{`// Dropdown container
<div className="animate-fade-in-down">
  {/* menu items */}
</div>

// Keyframe
@keyframes fade-in-down {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8. Progress Fill
// ---------------------------------------------------------------------------

function ProgressFillDemo() {
  const [key, setKey] = useState(0);
  const [value, setValue] = useState(75);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Progress bars animate from 0 to their target width using
        <code className="bg-muted px-1 rounded ml-1">.animate-progress-fill</code> which
        animates <code className="bg-muted px-1 rounded">scaleX(0) → scaleX(1)</code>.
      </Text>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setKey((k) => k + 1);
            setValue(Math.floor(Math.random() * 60) + 40);
          }}
        >
          Replay
        </Button>
        <Text className="text-sm font-mono w-12">{value}%</Text>
      </div>
      <div className="h-3 rounded-full bg-muted overflow-hidden">
        <div
          key={key}
          className="h-full rounded-full bg-primary animate-progress-fill"
          style={{ width: `${value}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Close Completion", pct: 87, color: "bg-success" },
          { label: "Budget Utilization", pct: 62, color: "bg-info" },
          { label: "Risk Score", pct: 34, color: "bg-warning" },
        ].map((bar) => (
          <div key={bar.label} className="space-y-1">
            <div className="flex justify-between text-xs">
              <span>{bar.label}</span>
              <span className="font-mono">{bar.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                key={key}
                className={cn("h-full rounded-full animate-progress-fill", bar.color)}
                style={{ width: `${bar.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <CodeSnippet>{`<div className="h-3 rounded-full bg-muted overflow-hidden">
  <div
    className="h-full rounded-full bg-primary animate-progress-fill"
    style={{ width: "75%" }}
  />
</div>`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 9. Toast Entry
// ---------------------------------------------------------------------------

function ToastDemo() {
  const [toasts, setToasts] = useState<{ id: number; message: string; type: string }[]>([]);
  const idRef = useRef(0);

  const addToast = (message: string, type: string) => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => {
      setToasts((t) => t.filter((toast) => toast.id !== id));
    }, 3000);
  };

  const removeToast = (id: number) => {
    setToasts((t) => t.filter((toast) => toast.id !== id));
  };

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Toast notifications slide in from the right using
        <code className="bg-muted px-1 rounded ml-1">.animate-slide-in-right</code>.
        They auto-dismiss after 3 seconds.
      </Text>
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => addToast("Invoice approved successfully", "success")}
        >
          <Bell className="h-3.5 w-3.5 mr-2" />
          Success Toast
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => addToast("Payment batch failed to process", "error")}
        >
          Error Toast
        </Button>
      </div>
      <div className="relative min-h-[120px] rounded-lg border border-dashed border-border bg-muted/20 overflow-hidden">
        <Text className="text-xs text-muted-foreground p-3">Toast preview area</Text>
        <div className="absolute top-3 right-3 flex flex-col gap-2 w-72">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={cn(
                "animate-slide-in-right rounded-lg border p-3 shadow-lg bg-background flex items-start gap-2",
                toast.type === "success" && "border-success-border",
                toast.type === "error" && "border-error-border"
              )}
            >
              <div
                className={cn(
                  "h-2 w-2 rounded-full mt-1.5 shrink-0",
                  toast.type === "success" ? "bg-success" : "bg-destructive"
                )}
              />
              <Text className="text-sm flex-1">{toast.message}</Text>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
      <CodeSnippet>{`// Toast container
<div className="animate-slide-in-right">
  {/* toast content */}
</div>

@keyframes slide-in-right {
  from { opacity: 0; transform: translateX(100%); }
  to   { opacity: 1; transform: translateX(0); }
}`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 10. Skeleton Shimmer
// ---------------------------------------------------------------------------

function SkeletonDemo() {
  const [loading, setLoading] = useState(true);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Skeleton loading uses a shimmer animation — a linear gradient that slides
        across the placeholder. Toggle to see the transition.
      </Text>
      <Button variant="outline" size="sm" onClick={() => setLoading(!loading)}>
        {loading ? "Show Content" : "Show Skeleton"}
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border p-4 space-y-3">
            {loading ? (
              <>
                <div className="h-4 w-3/4 rounded bg-muted skeleton-shimmer" />
                <div className="h-3 w-full rounded bg-muted skeleton-shimmer" />
                <div className="h-3 w-5/6 rounded bg-muted skeleton-shimmer" />
                <div className="h-8 w-24 rounded bg-muted skeleton-shimmer mt-2" />
              </>
            ) : (
              <>
                <Text className="font-semibold text-sm">Card Title {i}</Text>
                <Text className="text-xs text-muted-foreground">
                  This is the loaded content for card number {i}.
                </Text>
                <Button size="sm" variant="outline">View</Button>
              </>
            )}
          </div>
        ))}
      </div>
      <CodeSnippet>{`// Apply .skeleton-shimmer class to placeholder elements
<div className="h-4 w-3/4 rounded bg-muted skeleton-shimmer" />

// Shimmer uses a sliding gradient overlay
@keyframes shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 11. Attention Pulse
// ---------------------------------------------------------------------------

function AttentionPulseDemo() {
  const [active, setActive] = useState(true);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Critical alerts use <code className="bg-muted px-1 rounded">.animate-attention-pulse</code> to
        gently pulse the border color, drawing the user's eye without being jarring.
      </Text>
      <Button variant="outline" size="sm" onClick={() => setActive(!active)}>
        {active ? "Stop Pulse" : "Start Pulse"}
      </Button>
      <div
        className={cn(
          "rounded-lg border-2 border-error-border bg-error-bg p-4 flex items-start gap-3",
          active && "animate-attention-pulse"
        )}
      >
        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <Text className="font-semibold text-sm text-destructive">
            Critical: Reconciliation Mismatch
          </Text>
          <Text className="text-xs text-destructive/80 mt-1">
            Bank balance differs from GL by $23,456.78. Immediate review required.
          </Text>
        </div>
      </div>
      <CodeSnippet>{`@keyframes attention-pulse {
  0%, 100% {
    border-color: rgba(252, 165, 165, 0.6);
    box-shadow: 0 0 0 0 rgba(248, 113, 113, 0);
  }
  50% {
    border-color: rgba(248, 113, 113, 0.8);
    box-shadow: 0 0 12px 2px rgba(248, 113, 113, 0.3);
  }
}`}</CodeSnippet>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Micro-interactions Page
// ---------------------------------------------------------------------------

const sections = [
  { id: "button-press", icon: <MousePointerClick className="h-4 w-4" />, title: "Button Press", subtitle: "active:scale-[0.97] for tactile press feedback", component: <ButtonPressDemo /> },
  { id: "focus-rings", icon: <Focus className="h-4 w-4" />, title: "Focus Rings", subtitle: "Accessible focus indicators with soft ring glow", component: <FocusRingDemo /> },
  { id: "hover-states", icon: <Hand className="h-4 w-4" />, title: "Hover States", subtitle: "Smooth color transitions on interactive elements", component: <HoverStatesDemo /> },
  { id: "toggle-switch", icon: <ToggleRight className="h-4 w-4" />, title: "Toggle / Switch", subtitle: "Smooth knob translation with background color change", component: <ToggleSwitchDemo /> },
  { id: "checkbox-stroke", icon: <CheckSquare className="h-4 w-4" />, title: "Checkbox Stroke", subtitle: "SVG stroke-dashoffset animation for check mark drawing", component: <CheckboxStrokeDemo /> },
  { id: "accordion", icon: <ChevronsUpDown className="h-4 w-4" />, title: "Accordion Expand", subtitle: "CSS Grid height animation with chevron rotation", component: <AccordionDemo /> },
  { id: "dropdown", icon: <ChevronDown className="h-4 w-4" />, title: "Dropdown / Menu", subtitle: "fade-in-down entry animation for contextual menus", component: <DropdownDemo /> },
  { id: "progress", icon: <Loader2 className="h-4 w-4" />, title: "Progress Fill", subtitle: "scaleX animation from 0 to target width", component: <ProgressFillDemo /> },
  { id: "toast", icon: <Bell className="h-4 w-4" />, title: "Toast Entry", subtitle: "slide-in-right animation for notifications", component: <ToastDemo /> },
  { id: "skeleton", icon: <Sparkles className="h-4 w-4" />, title: "Skeleton Shimmer", subtitle: "Gradient overlay animation for loading placeholders", component: <SkeletonDemo /> },
  { id: "attention", icon: <AlertTriangle className="h-4 w-4" />, title: "Attention Pulse", subtitle: "Pulsing border and shadow for critical alerts", component: <AttentionPulseDemo /> },
];

export default function MicroInteractionsPage() {
  return (
    <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
      {/* ─── Header ─── */}
      <Section>
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Micro-interactions
            </Text>
            <Badge variant="outline">Interactive</Badge>
          </div>
          <Text className="text-base text-muted-foreground max-w-3xl">
            Small, purposeful animations that provide feedback, guide attention, and make the
            interface feel alive. Every interaction below uses CSS-only techniques — no JS
            animation runtime.
          </Text>
        </div>
      </Section>

      {/* ─── Quick Navigation ─── */}
      <Section>
        <div className="flex flex-wrap gap-2">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {s.icon}
              {s.title}
            </a>
          ))}
        </div>
      </Section>

      {/* ─── Demo Sections ─── */}
      {sections.map((s) => (
        <Section key={s.id} title={s.title}>
          <div id={s.id} className="scroll-mt-6">
            <SectionCard
              header={
                <>
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-primary/10 text-primary">{s.icon}</div>
                    <CardTitle className="text-sm">{s.title}</CardTitle>
                  </div>
                  <CardDescription>{s.subtitle}</CardDescription>
                </>
              }
              contentClassName="p-theme-5"
            >
              {s.component}
            </SectionCard>
          </div>
        </Section>
      ))}

      {/* ─── Summary ─── */}
      <Section title="Implementation Pattern">
        <SectionCard
          header={<CardTitle>CSS-First Decision Tree</CardTitle>}
          contentClassName="p-theme-5"
        >
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="shrink-0 mt-0.5">1</Badge>
              <Text>
                <strong>Hover/focus/active states</strong> → Use Tailwind transition utilities
                (<code className="bg-muted px-1 rounded text-xs">transition-colors</code>,
                <code className="bg-muted px-1 rounded text-xs">transition-all</code>)
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="shrink-0 mt-0.5">2</Badge>
              <Text>
                <strong>Enter/exit animations</strong> → Use keyframe classes
                (<code className="bg-muted px-1 rounded text-xs">.animate-fade-in-up</code>,
                <code className="bg-muted px-1 rounded text-xs">.animate-scale-in</code>)
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="shrink-0 mt-0.5">3</Badge>
              <Text>
                <strong>Height animations</strong> → Use CSS Grid trick
                (<code className="bg-muted px-1 rounded text-xs">grid-template-rows: 0fr → 1fr</code>)
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="shrink-0 mt-0.5">4</Badge>
              <Text>
                <strong>Staggered lists</strong> → Use
                <code className="bg-muted px-1 rounded text-xs">.stagger-children</code> parent class
              </Text>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="shrink-0 mt-0.5">5</Badge>
              <Text>
                <strong>Number counting</strong> → Use
                <code className="bg-muted px-1 rounded text-xs">useCountUp()</code> hook (only JS animation)
              </Text>
            </div>
          </div>
        </SectionCard>
      </Section>
    </div>
  );
}

export { MicroInteractionsPage };
